'use client';
import { useEffect, useMemo } from 'react';
import { gtmCrosssellProductImpression } from '@/utils/events/gtmEvents';
import CrossSellContents from '@/components/blocks/cross_sell/CrossSellContents';
import { useSelectedVariant } from '@/components/product/product-context';
import { useModal } from '@/components/modal/ModalProvider';
import offsetBlockClass from '@/utils/offsetBlockClass';
import { useCampaign } from '@/app/CampaignContext';

function getAvailableCrossSellProducts(products) {
  return (
    products?.filter((product) => {
      if (
        product.reference[0]?.shopifyProduct[0]?.metafields
          ?.find((metafield) => metafield?.key == 'product_out_of_stock')
          ?.value.toLowerCase() !== 'true'
      ) {
        return product;
      }
    }) ?? []
  );
}

export default function CrossSell({ block, position }) {
  const { cross_sell_label, products, show_offset_block_selector } =
    block.cross_sell.reference[0];

  const { uid } = block.cross_sell._metadata;
  const { variant } = useSelectedVariant();
  const { showPriceDetails } = useModal();
  const campaign = useCampaign();
  const crossSellProducts = useMemo(
    () => getAvailableCrossSellProducts(products),
    [products]
  );

  const filteredVariants = useMemo(() => {
    const selectedOptions = variant?.selectedOptions ?? [];
    const matchedCrossSellVariants = [];
    const selectedVariantSize = selectedOptions.find(
      (option) => option?.name == 'Size'
    )?.value;
    const selectedVariantSecondAttr = selectedOptions.find(
      (option) => option?.name !== 'Size'
    )?.value;

    //filter cross sell products by size and set cross sell
    crossSellProducts.forEach((item) => {
      const crossSellShopifyProduct = item.reference[0].shopifyProduct[0];
      let validVariant = '';
      //if product has standard variant (typically a pillow) and selected variant is twin/twinxl/queen, use standard variant
      if (item.single_size_product && item.secondary_attribute_override) {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) => `${item.secondary_attribute_override}` == variant.title
        );
      } else if (item.single_size_product && item.match_secondary_attribute) {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) => selectedVariantSecondAttr == variant.title
        );
      } else if (item.single_size_product) {
        validVariant = crossSellShopifyProduct.variants[0];
      } else if (item.match_secondary_attribute) {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) =>
            variant.title ==
            `${selectedVariantSize} / ${selectedVariantSecondAttr}`
        );
      } else if (
        crossSellShopifyProduct.variants.find(
          (variant) =>
            variant.title.split(' /')[0] === 'Standard' ||
            variant.title == 'Standard'
        ) !== undefined &&
        (selectedVariantSize == 'Twin' ||
          selectedVariantSize == 'Twin XL' ||
          selectedVariantSize == 'Full' ||
          selectedVariantSize == 'Queen')
      ) {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) =>
            variant.title.split(' /')[0] === 'Standard' ||
            variant.title == 'Standard'
        );
        //if product is a pillow or pillowcase, and selected variant is king/cal king, use to queen, king, or queen/king variant
      } else if (
        (crossSellShopifyProduct.productType == 'Pillow' ||
          crossSellShopifyProduct.productType == 'Pillowcase') &&
        (selectedVariantSize == 'King' || selectedVariantSize == 'Cal King')
      ) {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) =>
            variant.title.split('/')[0] === 'Queen' ||
            variant.title.split('/')[0] == 'King' ||
            variant.title.split(' /')[0] == 'Queen' ||
            variant.title.split(' /')[0] == 'King'
        );
      }
      //if there is a secondary attribute override set, use selected size / override
      else if (item.secondary_attribute_override) {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) =>
            `${selectedVariantSize} / ${item.secondary_attribute_override}` ==
            variant.title
        );
      }
      //otherwise use standard matching variant if available
      else {
        validVariant = crossSellShopifyProduct.variants.find(
          (variant) =>
            selectedVariantSize == variant.title.split('/')[0] ||
            selectedVariantSize == variant.title.split('/')[1] ||
            selectedVariantSize == variant.title.split(' /')[0] ||
            selectedVariantSize == variant.title
        );
      }
      if (validVariant) {
        validVariant['productTitle'] = item.reference[0].title.replace(
          'Leesa ',
          ''
        );
        validVariant['productHandle'] = crossSellShopifyProduct.handle;
        validVariant['productImage'] = validVariant.image.url;
        validVariant['productImageTitle'] =
          validVariant?.image?.altText ?? crossSellShopifyProduct.title;
        validVariant['type'] = crossSellShopifyProduct.productType;
        validVariant['outOfStockCopy'] = 'Out of stock';
        validVariant['productId'] = crossSellShopifyProduct?.id;
        matchedCrossSellVariants.push(validVariant);
      }
    });

    return matchedCrossSellVariants;
  }, [variant?.selectedOptions, crossSellProducts]);

  const hasValidVariants = filteredVariants.some((variant) => {
    const isOutOfStock =
      variant?.metafields
        ?.find((m) => m.key === 'variant_out_of_stock')
        ?.value?.toLowerCase() === 'true';
    return !isOutOfStock;
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && crossSellProducts.length) {
      const gaCollectionData = crossSellProducts;
      gtmCrosssellProductImpression(gaCollectionData);
    }
  }, [crossSellProducts]);

  return (
    <>
      {crossSellProducts?.length && hasValidVariants && (
        <section
          id={`${uid}_${position}`}
          data-name="cross-sell"
          className={`cross-sell text-input ${offsetBlockClass(
            show_offset_block_selector,
            'not-last:mb-8'
          )}`}
        >
          <div className="flex justify-between">
            <div className={`sh12 text-white`}>{cross_sell_label}</div>
            <div
              className={`text-dawn-grey-60 body-xs cursor-pointer underline`}
              onClick={() => {
                showPriceDetails(
                  `cross-sell-${uid}`,
                  null,
                  campaign?.pricing_details || ''
                );
              }}
            >
              Price details
            </div>
          </div>
          <CrossSellContents
            blockCrossSellProducts={crossSellProducts}
            filteredVariants={filteredVariants}
            position={position}
          />
        </section>
      )}
    </>
  );
}
